import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, ChevronUp, ArrowUpDown } from "lucide-react";
import clsx from "clsx";

export type SortDirection = "asc" | "desc";

export interface SortFieldConfig {
  key: string;
  label: string;
}

export interface SortOption<T extends string = string> {
  field: T;
  direction: SortDirection;
}

interface SortDropdownProps<T extends string = string> {
  onSortChange?: (sort: SortOption<T>) => void;
  currentSort?: SortOption<T>;
  fields: SortFieldConfig[];
  className?: string;
  label?: string;
}

export const SortDropdown = <T extends string = string>({
  onSortChange,
  currentSort,
  fields,
  className = "mb-4",
  label = "Sort by:",
}: SortDropdownProps<T>) => {
  const defaultSort: SortOption<T> = {
    field: fields[0]?.key as T,
    direction: "asc",
  };

  const activeSort = currentSort || defaultSort;

  const handleSortChange = (field: T, direction: SortDirection) => {
    onSortChange?.({ field, direction });
  };

  const getSortFieldLabel = (field: T) => {
    const fieldConfig = fields.find(f => f.key === field);
    return fieldConfig?.label || field;
  };

  const getSortDirectionLabel = (direction: SortDirection) => (direction === "asc" ? "Ascending" : "Descending");

  return (
    <div className={clsx("flex items-center justify-end gap-2", className)}>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center">
            <ArrowUpDown className="mr-2 h-4 w-4" />
            {getSortFieldLabel(activeSort.field)} ({getSortDirectionLabel(activeSort.direction)})
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          <DropdownMenuLabel>Sort by</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {fields.map(field => (
              <DropdownMenuItem
                key={field.key}
                onClick={() => handleSortChange(field.key as T, activeSort.direction)}
                className={clsx("mb-1 cursor-pointer", {
                  "bg-accent": activeSort.field === field.key,
                })}
              >
                {field.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Direction</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={() => handleSortChange(activeSort.field, "asc")}
              className={clsx("mb-1 cursor-pointer", {
                "bg-accent": activeSort.direction === "asc",
              })}
            >
              <ChevronUp className="mr-2 h-4 w-4" />
              Ascending
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleSortChange(activeSort.field, "desc")}
              className={clsx("cursor-pointer", {
                "bg-accent": activeSort.direction === "desc",
              })}
            >
              <ChevronDown className="mr-2 h-4 w-4" />
              Descending
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
